import React from 'react';
import { type UserImage } from '@/api/imageApi';
import { ImageRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import ImageTile from '@/components/ui/common/ImageTile';
import { useResponsiveColumns, IMAGE_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import VirtualizedGrid from '@/components/ui/common/VirtualizedGrid';

interface VirtualizedImageGridProps {
  /** Array of images to display */
  readonly images: readonly UserImage[];
  /** Whether initial loading is in progress */
  readonly isLoading?: boolean;
  /** Whether more items are being loaded */
  readonly isLoadingMore?: boolean;
  /** Handler for image click */
  readonly onImageClick?: (image: UserImage) => void;
  /** Custom class name */
  readonly className?: string;
  /** Whether to show user email badge (for admin views) */
  readonly showUserEmail?: boolean;
  /** Whether this is the favourites page (always show solid heart) */
  readonly isFavouritesPage?: boolean;
  /** Whether this is the archives page (hide pin button) */
  readonly isArchivesPage?: boolean;
  /** Handler for favourite toggle success */
  readonly onFavouriteSuccess?: (isFavourited: boolean) => void;
  /** Handler for archive toggle success */
  readonly onArchiveSuccess?: (isArchived: boolean) => void;
  /** Whether the user is an admin (shows archive button) */
  readonly isAdmin?: boolean;
}

/**
 * Virtualized image grid component for handling large numbers of images
 * Uses window-based virtualization to only render visible images
 * Optimized for galleries with 100k+ images
 * 
 * Used by both dashboard and admin content review pages.
 * 
 * Features:
 * - Only renders visible rows plus buffer
 * - Smooth scrolling with overscan
 * - Skeleton loading states
 * - Responsive column layout (1-4 columns)
 * - Memory efficient for massive datasets
 * - Optional user email badge display for admin views
 */
const VirtualizedImageGrid: React.FC<VirtualizedImageGridProps> = ({
  images,
  isLoading = false,
  isLoadingMore = false,
  onImageClick,
  className = '',
  showUserEmail = true,
  isFavouritesPage = false,
  isArchivesPage = false,
  onFavouriteSuccess,
  onArchiveSuccess,
  isAdmin = false,
}) => {
  const columns = useResponsiveColumns(IMAGE_GRID_COLUMNS);

  // Show skeleton grid during initial loading
  if (isLoading && images.length === 0) {
    return (
      <div className={className}>
        <div className="space-y-4">
          {['skeleton-row-0', 'skeleton-row-1', 'skeleton-row-2', 'skeleton-row-3'].map((key, i) => (
            <ImageRowSkeleton key={key} columns={columns} rowIndex={i} />
          ))}
        </div>
      </div>
    );
  }

  // No images to display
  if (images.length === 0) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      <VirtualizedGrid
        items={images}
        columns={columns}
        estimatedRowHeight={344} // ~320px for 1:1 card + 24px gap
        emptyAspectRatio="1/1"
        overscan={15}
        renderItem={(image) => (
          <ImageTile
            image={image}
            onClick={onImageClick}
            showUserEmail={showUserEmail}
            isFavouritesPage={isFavouritesPage}
            isArchivesPage={isArchivesPage}
            onFavouriteSuccess={onFavouriteSuccess}
            onArchiveSuccess={onArchiveSuccess}
            isAdmin={isAdmin}
          />
        )}
        getItemKey={(image) => `${image.id}-${image.status}-${image.updated_at}`}
      />

      {/* Loading more indicator with skeleton */}
      {isLoadingMore && (
        <div className="mt-4">
          <ImageRowSkeleton columns={columns} />
        </div>
      )}
    </div>
  );
};

export default VirtualizedImageGrid;
